import { CircleAlertIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import PlainFooterFieldset from '@/components/atoms/Fieldset/templates/PlainFooterFieldset';
import Link from '@/components/atoms/Link/Link';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { selectIframeView } from '@/redux/user/selectors';
import { FacebookError } from '@/shared/types/integration';
import { getDocsUrl } from '@/util/constants';

interface Props {
  onOAuthClick: () => void;
  type: string;
  error: FacebookError;
  previousFBConnection: string;
  isConnecting?: boolean;
}

const RedirectFieldset = ({
  onOAuthClick,
  error,
  type,
  previousFBConnection,
  isConnecting,
}: Props) => {
  const { t } = useTranslation();

  const suffix = type === 'facebook' ? 'facebook-pages' : 'instagram-accounts';
  const alreadyConnectedUrl = getDocsUrl(
    `/docs/integrations/${type}-messenger#connecting-more-than-one-${suffix}`
  );
  const isIframeView = useSelector(selectIframeView);

  return (
    <PlainFooterFieldset
      title={t('integrations.facebook.redirect.title', { 0: type })}
      subtitle={isIframeView ? '' : t('integrations.facebook.redirect.subtitle', { 0: type })}
      primaryButton={{
        isLoading: isConnecting,
        children: t('integrations.connect'),
        onClick: onOAuthClick,
      }}
      fullContentWidth
    >
      {!error?.message && previousFBConnection && type === 'facebook' && (
        <Alert variant="warning">
          <TriangleAlertIcon />
          <AlertTitle>{t('integrations.facebook.oauth.title', { 0: type })}</AlertTitle>
          <AlertDescription>
            <p>
              {t('integrations.facebook.oauth.contextual_help_p1', {
                0: type,
                1: previousFBConnection,
              })}
            </p>
            <p>{t('integrations.facebook.oauth.contextual_help_p2')}</p>
            {isIframeView ? null : (
              <Link href={alreadyConnectedUrl} external>
                {t('common.learn_more')}
              </Link>
            )}
          </AlertDescription>
        </Alert>
      )}
      {error?.message && (
        <Alert variant="destructive">
          <CircleAlertIcon />
          <AlertDescription>
            <p>{error?.message}</p>
            {error.url && (
              <a href={error.url} target="_blank" rel="noopener">
                {t('common.learn_more')}
              </a>
            )}
          </AlertDescription>
        </Alert>
      )}
      {!error?.message && type === 'instagram' && (
        <Alert variant="info">
          <InfoIcon />
          <AlertDescription>
            <p>{t('integrations.facebook.instagram_info')}</p>
            <Link href={getDocsUrl(`/docs/integrations/${type}-messenger`)} external>
              {t('common.learn_more')}
            </Link>
          </AlertDescription>
        </Alert>
      )}
    </PlainFooterFieldset>
  );
};

export default RedirectFieldset;
